import React, { useState, useEffect } from "react"
import styled from "styled-components"
import TrashIcon from "@assets/icons/Trash.svg"

const ALL_PROPERTY_STATUSES = [{ label: "Loué" }, { label: "Disponible" }, { label: "Fermé" }]

// ---------- Styled Components ----------
const CardContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.secondary};
  border-radius: ${({ theme }) => theme.components.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.white};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: ${({ theme }) => theme.spacing.md};
  width: 700px;

  @media screen {
    width: 100%;
  }
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const TitleSpan = styled.h2`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.primary};
  margin: 0.4rem 0;
`
const Details = styled.div`
  width: 100%;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.primaryText};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`

export const EditButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.primary};
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.primary};
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  margin-left: 0.2rem;

  svg {
    display: flex;
    max-width: 1.3rem;
    max-height: fit-content;
    /* margin: auto; */
    /* color: ${({ theme }) => theme.primary}; */
    /* fill: currentColor; */

    path {
      stroke: currentColor;
    }
  }
`

const SaveButton = styled.button`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`

const CancelButton = styled.button`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.secondary};
  color: ${({ theme }) => theme.primary};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`

const EditInput = styled.input`
  padding: ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ theme }) => theme.secondary};
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const SelectField = styled.select`
  padding: ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ theme }) => theme.secondary};
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: 0 ${({ theme }) => theme.spacing.md};
  }
`

const ModalContent = styled.div`
  background: ${({ theme }) => theme.white};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.components.borderRadius.medium};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
`

const ModalHeader = styled.h3`
  font-size: 1.2rem;
  margin: 0;
`

const ModalBody = styled.p`
  font-size: 1rem;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
`

// ---------- Type ----------
export interface PropertyFeatureCardData {
  id: string
  address: string
  postalCode: string
  city: string
  description: string
  status: string
}

// ---------- Component Props ----------
interface PropertyCardProps {
  data: PropertyFeatureCardData
  onEdit?: (id: string, partialData: Partial<PropertyFeatureCardData>) => void
  onRemove?: (id: string) => void
}

// ---------- PropertyCard Component ----------
const PropertyCard: React.FC<PropertyCardProps> = ({ data, onEdit, onRemove }) => {
  const { id, address, postalCode, city, description, status } = data
  const [isEditing, setIsEditing] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Local state for editing
  const [editAddress, setEditAddress] = useState(address)
  const [editPostalCode, setEditPostalCode] = useState(postalCode)
  const [editCity, setEditCity] = useState(city)
  const [editDescription, setEditDescription] = useState(description)
  const [editStatus, setEditStatus] = useState(status)

  // Re-sync local state when props change and not editing
  useEffect(() => {
    if (!isEditing) {
      setEditAddress(address)
      setEditPostalCode(postalCode)
      setEditCity(city)
      setEditDescription(description)
      setEditStatus(status)
    }
  }, [address, postalCode, city, description, status, isEditing])

  const handleSave = () => {
    onEdit &&
      onEdit(id, {
        address: editAddress,
        postalCode: editPostalCode,
        city: editCity,
        description: editDescription,
        status: editStatus,
      })
    setIsEditing(false)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  return (
    <CardContainer>
      <HeaderRow>
        {isEditing ? (
          <EditInput type="text" value={editAddress} onChange={(e) => setEditAddress(e.target.value)} />
        ) : (
          <TitleSpan>{address}</TitleSpan>
        )}
        {isEditing ? (
          <>
            <EditButton onClick={() => setIsModalOpen(true)}>
              <TrashIcon />
            </EditButton>
            {isModalOpen && (
              <ModalOverlay>
                <ModalContent>
                  <ModalHeader>Confirmation</ModalHeader>
                  <ModalBody>Êtes-vous sûr de vouloir supprimer cette propriété ?</ModalBody>
                  <ModalFooter>
                    <CancelButton onClick={() => setIsModalOpen(false)}>Annuler</CancelButton>
                    <SaveButton
                      onClick={() => {
                        onRemove && onRemove(id)
                        setIsModalOpen(false)
                      }}
                    >
                      Supprimer
                    </SaveButton>
                  </ModalFooter>
                </ModalContent>
              </ModalOverlay>
            )}
          </>
        ) : (
          <>
            <EditButton onClick={() => setIsEditing(true)}>Modifier</EditButton>
          </>
        )}
      </HeaderRow>
      {isEditing ? (
        <>
          <EditInput type="text" placeholder="Code postal" value={editPostalCode} onChange={(e) => setEditPostalCode(e.target.value)} />
          <EditInput type="text" placeholder="Ville" value={editCity} onChange={(e) => setEditCity(e.target.value)} />
          <EditInput type="text" placeholder="Description" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
          <SelectField value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
            {ALL_PROPERTY_STATUSES.map((s) => (
              <option key={s.label} value={s.label}>
                {s.label}
              </option>
            ))}
          </SelectField>
          <ButtonGroup>
            <SaveButton onClick={handleSave}>Sauvegarder</SaveButton>
            <CancelButton onClick={handleCancel}>Annuler</CancelButton>
          </ButtonGroup>
        </>
      ) : (
        <>
          <Details>
            <strong>Code postal:</strong> {postalCode}
          </Details>
          <Details>
            <strong>Ville:</strong> {city}
          </Details>
          <Details>
            <strong>Description:</strong> {description}
          </Details>
          <Details>
            <strong>Statut:</strong> {status}
          </Details>
        </>
      )}
    </CardContainer>
  )
}

export default PropertyCard
